import {
	AI_REVIEW_CONTENT_TYPES,
	DOCUMENT_MAX_BYTES,
	type ProjectDTO,
} from "@zplanem/responses"
import type React from "react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import { formatBytes } from "../../../lib/format-bytes"
import { useCaptureInvoice } from "../../hooks/use-budget"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

/**
 * Flow-B invoice capture (ZPL-224): pick a PDF → capture → PUT → confirm.
 * On success shows the soft duplicate warning (if any) before closing.
 */
export function CaptureInvoiceModal({
	propertyId,
	projects,
	onClose,
	lockedProjectId,
}: {
	propertyId: string
	projects: ProjectDTO[]
	onClose: () => void
	/** Project-scoped view (ZPL-224): capture is pinned to this project and the
	 *  project selector is hidden. */
	lockedProjectId?: string
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tb = t.projects.budget
	const [file, setFile] = useState<File | null>(null)
	const [projectId, setProjectId] = useState(lockedProjectId ?? "")
	const [error, setError] = useState<string | null>(null)
	const [duplicate, setDuplicate] = useState(false)
	const capture = useCaptureInvoice(propertyId)

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	async function submit(e: React.FormEvent) {
		e.preventDefault()
		if (!file) return
		setError(null)
		if (file.size > DOCUMENT_MAX_BYTES) {
			setError(tb.errorFileTooLarge)
			return
		}
		try {
			const result = await capture.mutateAsync({
				file,
				projectId: projectId || null,
			})
			if (result.possibleDuplicate) {
				// Soft warning (Decyzja 7): non-blocking — surface it, let the owner
				// close; the entry already exists either way.
				setDuplicate(true)
				return
			}
			onClose()
		} catch (err) {
			setError(err instanceof ApiError ? err.message : tb.errorSave)
		}
	}

	const inputClass =
		"w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-ring"

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={tb.captureModalTitle}
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
		>
			<button
				type="button"
				aria-hidden="true"
				tabIndex={-1}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<form
				onSubmit={submit}
				className="relative w-full max-w-md rounded-lg border border-border bg-popover p-6 flex flex-col gap-4"
			>
				<h2 className="text-lg font-bold text-foreground">
					{tb.captureModalTitle}
				</h2>
				<p className="text-sm text-muted-foreground">{tb.captureHint}</p>

				{duplicate ? (
					<>
						<p role="alert" className="text-sm text-warning">
							{tb.duplicateWarning}
						</p>
						<div className="flex justify-end">
							<Button type="button" onClick={onClose}>
								{tb.cancel}
							</Button>
						</div>
					</>
				) : (
					<>
						<label className="flex flex-col gap-1 text-sm text-muted-foreground">
							{tb.fileLabel}
							<input
								type="file"
								accept={AI_REVIEW_CONTENT_TYPES.join(",")}
								onChange={(e) => setFile(e.target.files?.[0] ?? null)}
								className={inputClass}
							/>
						</label>
						{file ? (
							<p className="text-xs text-muted-foreground">
								{file.name} · {formatBytes(file.size)}
							</p>
						) : null}

						{lockedProjectId ? null : (
							<label className="flex flex-col gap-1 text-sm text-muted-foreground">
								{tb.projectLabel}
								<select
									value={projectId}
									onChange={(e) => setProjectId(e.target.value)}
									className={inputClass}
								>
									<option value="">{tb.projectNone}</option>
									{projects.map((p) => (
										<option key={p.id} value={p.id}>
											{p.name}
										</option>
									))}
								</select>
							</label>
						)}

						{error ? (
							<p role="alert" className="text-sm text-destructive">
								{error}
							</p>
						) : null}

						<div className="flex justify-end gap-2">
							<Button type="button" variant="ghost" onClick={onClose}>
								{tb.cancel}
							</Button>
							<Button type="submit" disabled={capture.isPending || !file}>
								{capture.isPending ? tb.capturing : tb.capture}
							</Button>
						</div>
					</>
				)}
			</form>
		</div>
	)
}
